import { HttpError } from '@euler/lib/request/HttpError';

/**
 * Tells whether an error from the startup session check means we never got a
 * verdict from the server (offline, timeout, gateway/server failure), as
 * opposed to the server actually answering about the session.
 *
 * A 401 counts as unreachable only when the reactive refresh itself failed to
 * complete — the refresh token may well still be good.
 */
export function isServerUnreachableError(e: unknown): boolean {
  // fetch() rejects with TypeError on network failure and AbortError on our
  // request timeout; neither is an HttpError.
  if (!(e instanceof HttpError)) return true;
  const { status } = e;
  if (status === 0 || status === 408 || status === 429 || status >= 500) {
    return true;
  }
  return status === 401 && e.authRefreshStatus === 'refresh-failed';
}

/**
 * A stored session is viable when the server could still accept it: either
 * the access token hasn't expired, or a refresh token exists that hasn't.
 */
export function isSessionViable(params: {
  isAccessTokenValid: boolean;
  refreshToken: string | null | undefined;
  refreshTokenExpiresAt: number | null | undefined;
  now?: number;
}): boolean {
  const { isAccessTokenValid, refreshToken, refreshTokenExpiresAt } = params;
  if (isAccessTokenValid) return true;
  if (!refreshToken) return false;
  return (
    refreshTokenExpiresAt == null ||
    refreshTokenExpiresAt > (params.now ?? Date.now())
  );
}
